import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import NewsletterForm from '../components/NewsletterForm';
import CategoryIcon from '../components/CategoryIcon';
import { getActiveBanner, SEASONS } from '../data/seasonalBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { t, language } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [featuredSpotlight, setFeaturedSpotlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const banner = (() => {
    // Allow ?season=summer|spring|autumn|winter for previewing seasonal banners
    if (typeof window !== 'undefined') {
      const param = new URLSearchParams(window.location.search).get('season');
      if (param && SEASONS[param]) return SEASONS[param];
    }
    return getActiveBanner();
  })();
  const now = new Date();
  const monthLabel = {
    nl: now.toLocaleString('nl-NL', { month: 'long', year: 'numeric' }),
    en: now.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
    fr: now.toLocaleString('fr-FR', { month: 'long', year: 'numeric' }),
  };

  const fetchData = useCallback(async () => {
    try {
      const [categoriesRes, spotlightRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/spotlights/featured`).catch(() => ({ data: null })),
      ]);
      setCategories(categoriesRes.data);
      setFeaturedSpotlight(spotlightRes.data || null);
    } catch (error) {
      console.error('Failed to load home data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* SEO */}
      <SEO
        title={t(
          'U&Fashion | Stijlvolle mode voor dames 35+ | Gecureerd uit Europa',
          'U&Fashion | Stylish Fashion for Women 35+ | Curated from Europe',
          'U&Fashion | Mode élégante pour femmes 35+ | Sélectionnée en Europe'
        )}
        description={t(
          'Zorgvuldig gecureerde mode voor de vrouw van 35 tot 55, uit de beste Europese webshops. Tijdloze musthaves — geen tienerhypes.',
          'Carefully curated fashion for women aged 35 to 55, from the best European webshops. Timeless must-haves — no teen hypes.',
          'Une mode soigneusement sélectionnée pour la femme de 35 à 55 ans, issue des meilleures boutiques européennes. Des essentiels intemporels — sans les modes ados.'
        )}
        keywords={t(
          'mode 35+, stijlvolle mode dames 40+, tijdloze mode, gecureerde fashion, premium mode Europa, kleding 45+, dameskleding stijlvol, fashion curator, tijdloze basics dames',
          'fashion 35+, stylish clothing women 40+, timeless fashion, curated fashion, premium European fashion, clothing 45+, stylish women clothing, fashion curator, timeless basics women',
          'mode 35+, mode élégante femmes 40+, mode intemporelle, mode sélectionnée, mode premium Europe, vêtements 45+, vêtements femme élégants, curatrice mode, essentiels intemporels femmes'
        )}
        ogTitle={t(
          'U&Fashion | Stijlvolle mode voor dames 35+',
          'U&Fashion | Stylish Fashion for Women 35+',
          'U&Fashion | Mode élégante pour femmes 35+'
        )}
        ogDescription={t(
          'Zorgvuldig gecureerde premium fashion voor de stijlbewuste vrouw van 35 tot 55.',
          'Carefully curated premium fashion for the style-conscious woman aged 35 to 55.',
          'Une mode premium soigneusement sélectionnée pour la femme élégante de 35 à 55 ans.'
        )}
        ogImage="https://images.pexels.com/photos/24245318/pexels-photo-24245318.jpeg"
        canonical={typeof window !== 'undefined' ? window.location.origin + '/' : 'https://uandfashion.eu/'}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'U&Fashion — Fashion collectie van topmerken',
          url: 'https://uandfashion.eu/',
          description: 'Zorgvuldig samengestelde collectie kleding, schoenen, designer items, lingerie en accessoires van topmerken — met regelmatig acties bij onze partner-webshops.',
          inLanguage: ['nl-NL', 'en-US'],
          isPartOf: { '@type': 'WebSite', name: 'U&Fashion', url: 'https://uandfashion.eu/' },
        }}
      />

      {/* Hero Section */}
      <section className="relative h-[640px] md:h-[720px] flex items-start justify-start overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_lingua-assist-6/artifacts/y2y9mzny_Uandfashionhomepage%20%281%29.webp"
            alt={t('U&Fashion — fashion deals en designer kleding met korting', 'U&Fashion — fashion deals and designer clothing with discount')}
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 38%' }}
            width="2048"
            height="1365"
            fetchPriority="high"
            decoding="async"
          />
          {/* Soft side gradient — just enough to anchor the card on the left */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent"></div>
        </div>

        {/* Hero Content — frosted glass card */}
        <div className="relative z-10 w-full px-4 sm:px-6 md:px-10 lg:px-16 pt-16 md:pt-20" data-testid="hero-content">
          <div className="max-w-xl bg-black/40 backdrop-blur-md rounded-2xl p-8 sm:p-10 border border-white/10 shadow-2xl">
            <div className="flex items-center space-x-2 mb-4">
              <Sparkles className="w-5 h-5 text-pink-300" />
              <span className="text-pink-300 font-medium uppercase tracking-[0.2em] text-xs">
                {t('Curated Fashion', 'Curated Fashion', 'Curated Fashion')}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-light mb-5 leading-[1.15] tracking-tight text-white">
              {t(
                'Stijl kent geen leeftijd.',
                'Style knows no age.',
                "Le style n'a pas d'âge."
              )}
            </h1>
            <p className="text-base md:text-lg mb-8 text-gray-100 font-light leading-relaxed">
              {t(
                'Zorgvuldig gecureerde mode voor de vrouw van 35 tot 55, uit de beste Europese webshops.',
                'Carefully curated fashion for women aged 35 to 55, from the best European webshops.',
                'Une mode soigneusement sélectionnée pour la femme de 35 à 55 ans, issue des meilleures boutiques européennes.'
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#categories"
                className="bg-white text-gray-900 px-6 py-3 rounded-full font-medium hover:bg-gray-100 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg"
                data-testid="hero-cta-primary"
              >
                <span>{t('Ontdek collecties', 'Discover collections', 'Découvrir les collections')}</span>
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#about-platform"
                className="border border-white/50 text-white px-6 py-3 rounded-full font-medium hover:bg-white/10 transition-all duration-300 text-center"
                data-testid="hero-cta-secondary"
              >
                {t('Over ons platform', 'About our platform', 'À propos de la plateforme')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Slim editorial divider between hero and seasonal banner */}
      <div
        className="bg-gray-950 border-t border-b border-gray-800 py-3 text-center"
        data-testid="hero-banner-divider"
      >
        <p className="text-[12px] md:text-[13px] uppercase tracking-[0.35em] text-gray-400 font-light">
          {t(
            `— ${banner.label_nl} · ${monthLabel.nl} —`,
            `— ${banner.label_en} · ${monthLabel.en} —`,
            `— ${banner.label_fr || banner.label_en} · ${monthLabel.fr} —`
          )}
        </p>
      </div>

      {/* Editorial Glamour Banner — auto-rotating per season */}
      <section className="relative h-[560px] md:h-[720px] overflow-hidden" data-testid="glamour-banner">
        <img
          src={banner.image}
          alt={t(
            `${banner.label_nl} — modieuze collecties bij U&Fashion`,
            `${banner.label_en} — curated collections at U&Fashion`,
            `${banner.label_fr || banner.label_en} — collections soigneusement sélectionnées chez U&Fashion`
          )}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover ${banner.image_position || 'object-center'}`}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20"></div>
        <div className="relative z-10 h-full flex items-center justify-start text-left px-6 md:px-12 lg:px-20">
          <div className="max-w-xl text-white" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.55)' }}>
            <p className="text-xs uppercase tracking-[0.3em] text-white/80 mb-4" data-testid="banner-season-label">
              {t(`— ${banner.label_nl} —`, `— ${banner.label_en} —`, `— ${banner.label_fr || banner.label_en} —`)}
            </p>
            <p className="text-2xl md:text-3xl lg:text-4xl font-light italic leading-snug">
              {t(banner.quote_nl, banner.quote_en, banner.quote_fr || banner.quote_en)}
            </p>
          </div>
        </div>
      </section>

      {/* Brand Spotlight Teaser */}
      <section className="bg-white pt-12 pb-12 border-t border-gray-100" data-testid="brand-spotlight-teaser">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-pink-500 mb-3">
            {t('Merkenverhalen', 'Brand stories', 'Histoires de marques')}
          </p>
          <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-5 tracking-tight">
            {t('Ontdek merken die passen bij jouw stijl', 'Discover brands that match your style', 'Découvrez les marques qui correspondent à votre style')}
          </h2>
          <p className="text-base text-gray-600 leading-relaxed mb-6 max-w-2xl mx-auto">
            {t(
              'Achter elke collectie zit een verhaal. Ontdek de stijl, visie en identiteit van zorgvuldig geselecteerde fashion brands en laat je inspireren door hun unieke aanpak.',
              'Behind every collection is a story. Discover the style, vision and identity of carefully selected fashion brands and find inspiration in their unique approach.',
              'Derrière chaque collection se cache une histoire. Découvrez le style, la vision et l\u2019identité de marques mode soigneusement sélectionnées et laissez-vous inspirer par leur approche unique.'
            )}
          </p>
          {featuredSpotlight && (
            <p className="text-sm text-gray-500 mb-8" data-testid="featured-spotlight-line">
              <span className="uppercase tracking-[0.2em] text-xs text-gray-400 mr-2">
                {t('Nu in de kijker:', 'Currently featured:', 'À l\u2019honneur :')}
              </span>
              <span className="text-gray-900 font-medium">{featuredSpotlight.brand_name}</span>
            </p>
          )}
          <Link
            to="/ontdek-merken"
            className="inline-flex items-center gap-2 bg-gray-900 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
            data-testid="discover-brands-cta"
          >
            {t('Ontdek het verhaal achter de merken', 'Discover the stories behind the brands', 'Découvrez l\u2019histoire derrière les marques')}
            <ArrowRight className="w-4 h-4" />
          </Link>
          {featuredSpotlight && (
            <div className="mt-5">
              <a
                href={featuredSpotlight.collection_url}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="text-sm text-gray-500 hover:text-gray-900 underline underline-offset-4 transition-colors"
                data-testid="featured-collection-link"
              >
                {t(
                  `Bekijk de collectie van ${featuredSpotlight.brand_name}`,
                  `View the ${featuredSpotlight.brand_name} collection`,
                  `Découvrir la collection ${featuredSpotlight.brand_name}`
                )}
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Curation Statement — independent platform positioning (compact) */}
      <section id="about-platform" className="bg-gray-50 py-8" data-testid="curation-statement">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">
            {t('Onafhankelijk platform', 'Independent platform', 'Plateforme indépendante')}
          </p>
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed font-light">
            {t(
              'Een doordachte selectie van merken — gekozen op stijl, kwaliteit en identiteit. Geen eindeloze catalogus, maar inspiratie voor elke gelegenheid.',
              'A thoughtful selection of brands — chosen for style, quality and identity. No endless catalogue, but inspiration for every occasion.',
              'Une sélection réfléchie de marques — choisies pour leur style, leur qualité et leur identité. Pas un catalogue interminable, mais une inspiration pour chaque occasion.'
            )}
          </p>
        </div>
      </section>

      {/* Categories Section */}
      <section id="categories" className="py-20 bg-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('Shop per Categorie', 'Shop by Category', 'Acheter par catégorie')}
            </h2>
            <p className="text-xl text-gray-600">
              {t(
                'Ontdek onze zorgvuldig geselecteerde collecties',
                'Discover our carefully curated collections',
                'Découvrez nos collections soigneusement sélectionnées'
              )}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/category/${category.slug}`}
                  className="group bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  data-testid={`category-card-${category.slug}`}
                >
                  <div className="mb-3 flex justify-center">
                    <CategoryIcon slug={category.slug} size={36} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-pink-500 transition-colors">
                    {(language === 'nl' && category.name_nl) ||
                      (language === 'fr' && (category.name_fr || category.name_en)) ||
                      category.name_en}
                  </h3>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA — bottom-of-page secondary conversion */}
      <section className="py-16 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500" data-testid="newsletter-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <p className="text-xs uppercase tracking-[0.3em] text-white/80 mb-3">
            {t('Nieuwsbrief', 'Newsletter', 'Newsletter')}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t(
              'Blijf op de hoogte',
              'Stay in the loop',
              'Restez informé'
            )}
          </h2>
          <p className="text-lg md:text-xl mb-8 text-white/90 max-w-2xl mx-auto">
            {t(
              'Schrijf je in op onze nieuwsbrief en ontdek als eerste de nieuwste acties, seizoenstrends en merkverhalen.',
              'Subscribe to our newsletter and be the first to discover the latest deals, seasonal trends and brand stories.',
              'Inscrivez-vous à notre newsletter et soyez le premier à découvrir les dernières promotions, tendances saisonnières et histoires de marques.'
            )}
          </p>
          <NewsletterForm />
        </div>
      </section>

      {/* Featured Products and Editor's Picks removed (2026-05-31):
          - New flow: Hero → Curated strip → Seasonal → Brand Spotlight → Onafhankelijk platform → Categories → Newsletter
          - Editorial inspiration first, commercial action in the middle, soft conversion at the bottom */}
    </div>
  );
};

export default Home;